import { notify } from '../ui/notifications.tsx'
import {
  Calendar,
  ChartPie,
  Download,
  Edit,
  Globe,
  IdCard,
  Trash2,
  TriangleAlert,
  Users,
} from 'lucide-preact'
import type { ComponentChildren } from 'preact'
import { isAdmin } from '../lib/session.ts'
import { computed, effect } from '@preact/signals'
import {
  handleUnlimitedDuration,
  handleUnlimitedInput,
  LicenseSelect,
} from './license.tsx'
import { api, type ApiOutput } from '../lib/api.ts'
import { ErrorWithData } from '@01edu/api-client'
import { A, navigate, url } from '@01edu/signal-router'
import { onEvent } from '../lib/session.ts'
import { DialogModal } from '../ui/dialog.tsx'
import { formatDate, getDateInputValue } from '../lib/date-utils.ts'
import { Donut, MeterLegend } from '../ui/meter.tsx'
import { ProjectDomain } from '../ui/project-domain.tsx'
import { daysLeft } from '../lib/status.ts'

type LicenseDetails = ApiOutput['GET/api/project/license']
type ScopeUsage = ApiOutput['GET/api/license/scopes'][number]
type LicenseParticipant = ApiOutput['GET/api/license/participants'][number]

const getLicenseDetails = api['GET/api/project/license'].signal()
const getLicenseScopes = api['GET/api/license/scopes'].signal()
const getLicenseParticipants = api['GET/api/license/participants'].signal()
const deleteLicense = api['DELETE/api/project/license'].signal()

onEvent(
  'LICENSE_DELETED',
  () => {
    navigate({
      params: { project: url.params.project, dialog: null, license: null },
      replace: true,
    })
    getLicenseDetails.reset()
    notify({
      status: 'success',
      title: 'License Deleted',
      description: 'The license has been successfully deleted',
    })
  },
)

const updateLicense = api['PUT/api/project/license'].signal()
const clearValidity = (e: Event) =>
  (e.target as HTMLInputElement).setCustomValidity('')

const selectedScopeId = computed(() => {
  const id = url.params.scope
  return id ? Number(id) : undefined
})

const refreshLicense = () => {
  const licenseId = url.params.license
  const projectId = url.params.project

  if (licenseId && projectId) {
    getLicenseDetails.fetch({ license: licenseId })
    getLicenseScopes.fetch({ license: licenseId })
  }
}

effect(refreshLicense)

effect(() => {
  const details = getLicenseDetails.data
  if (details) {
    getLicenseParticipants.fetch({
      license: String(details.licenseId),
      scope: selectedScopeId.value ? String(selectedScopeId.value) : undefined,
    })
  }
})

const getLicense = () => getLicenseDetails.data
const getScopes = () => getLicenseScopes.data
const getParticipants = () => getLicenseParticipants.data

const selectedScope = computed<ScopeUsage | undefined>(() => {
  const id = selectedScopeId.value
  const scopes = getScopes()
  if (id === undefined || !scopes) return undefined
  return scopes.find((s) => s.scopeId === id)
})

const DeleteLicenseModal = () => (
  <DialogModal id='delete-license'>
    <h3 class='text-lg font-bold text-error'>Delete License</h3>
    <div class='py-4'>
      <div class='alert alert-warning mb-4'>
        <TriangleAlert />
        <div>
          <h3 class='font-bold'>Warning!</h3>
          <div class='text-xs'>
            This action cannot be undone. This will permanently delete the
            license.
          </div>
        </div>
      </div>

      <div class='flex gap-3 justify-end'>
        <A
          params={{ dialog: null }}
          class='btn btn-ghost'
          replace
        >
          Cancel
        </A>
        <button
          type='button'
          onClick={() => {
            const license = getLicense()
            if (license) {
              deleteLicense.fetch({
                license: String(license.licenseId),
              })
            }
          }}
          class='btn btn-error'
          disabled={!!deleteLicense.pending}
        >
          {deleteLicense.pending
            ? (
              <>
                <span class='loading loading-spinner loading-sm' />
                Deleting...
              </>
            )
            : (
              <>
                <Trash2 class='h-4 w-4' />
                Delete License
              </>
            )}
        </button>
      </div>

      {deleteLicense.error && (
        <div class='alert alert-error mt-4'>
          <svg
            class='stroke-current shrink-0 h-6 w-6'
            fill='none'
            viewBox='0 0 24 24'
          >
            <path
              stroke-linecap='round'
              stroke-linejoin='round'
              stroke-width='2'
              d='M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
          <span>{deleteLicense.error.message}</span>
        </div>
      )}
    </div>
  </DialogModal>
)

const updateErrorReported = computed(() => {
  const { error } = updateLicense
  if (!(error instanceof ErrorWithData)) return false
  const input = document.querySelector(
    `#update-license input[name=${error.data.input}]`,
  )
  if (!(input instanceof HTMLInputElement)) return false
  input.setCustomValidity(error.message)
  input.reportValidity()
  return true
})

const handleSubmit = async (e: Event) => {
  e.preventDefault()

  const projectId = url.params.project
  const license = getLicense()
  if (!projectId || !license) {
    return
  }

  const form = e.target as HTMLFormElement
  const licenseNameInput = form.licenseName as HTMLInputElement
  const unlimitedInput = form.unlimited as HTMLInputElement
  const licenseCapacityInput = form.licenseCapacity as HTMLInputElement
  const licenseStartAtInput = form.licenseStartAt as HTMLInputElement
  const licenseEndAtInput = form.licenseEndAt as HTMLInputElement
  const licenseScopeInput = form.licenseScopes as HTMLInputElement
  const unlimitedDurationInput = form.unlimitedDuration as HTMLInputElement

  const licenseScopes = licenseScopeInput.value
    ? licenseScopeInput.value.split(',')
    : []

  const licenseCapacity = unlimitedInput.checked
    ? -1
    : Number(licenseCapacityInput.value)

  await updateLicense.fetch({
    license: String(license.licenseId),

    licenseName: licenseNameInput.value.trim() || undefined,
    licenseCapacity,
    licenseStartAt: Math.round(licenseStartAtInput.valueAsNumber / 1000),
    licenseEndAt: unlimitedDurationInput.checked
      ? -1
      : Math.round(licenseEndAtInput.valueAsNumber / 1000),
    licenseScopes,
  })

  if (!updateLicense.error) {
    navigate({ params: { dialog: null }, replace: true })
    refreshLicense()
    notify({
      status: 'success',
      title: 'License Updated',
      description: 'The license has been successfully updated',
    })
  }
}

const UpdateLicenseModal = () => {
  return (
    <DialogModal id='update-license'>
      <h3 class='text-lg font-bold'>Update License</h3>
      <form onSubmit={handleSubmit} class='py-4 fieldset gap-4'>
        <label class='floating-label w-full'>
          <input
            name='licenseName'
            type='text'
            placeholder='License Name'
            class='input input-md w-full'
            onInput={clearValidity}
            maxLength={50}
            defaultValue={getLicense()?.licenseName ?? undefined}
          />
          <span>License Name</span>
        </label>
        <div class='form-control w-full border-2 border-dashed border-base-300 rounded-box p-4 bg-base-100'>
          <label class='cursor-pointer label justify-between'>
            <span class='label-text text-lg font-semibold'>
              Unlimited License
            </span>
            <input
              type='checkbox'
              name='unlimited'
              class='checkbox checkbox-primary checkbox-lg'
              onInput={handleUnlimitedInput}
              defaultChecked={getLicense()?.licenseCapacity === -1}
            />
          </label>
          <p class='text-sm text-base-content/70 mt-1'>
            Enable for unlimited seats access
          </p>
        </div>
        <label class='floating-label w-full'>
          <input
            name='licenseCapacity'
            type='number'
            placeholder='License Capacity'
            class='input input-md w-full'
            onInput={clearValidity}
            disabled={getLicense()?.licenseCapacity === -1}
            required
            min={1}
            defaultValue={getLicense()?.licenseCapacity !== -1
              ? getLicense()?.licenseCapacity
              : undefined}
          />
          <span>License Capacity</span>
        </label>

        <label class='floating-label w-full'>
          <LicenseSelect defaultScopes={getScopes()?.map((v) => v.path)} />
          <span>License Scope</span>
        </label>

        <label class='floating-label w-full'>
          <input
            name='licenseStartAt'
            type='date'
            class='input input-md w-full'
            onInput={clearValidity}
            required
            defaultValue={getDateInputValue(getLicense()?.licenseStartAt)}
          />
          <span>Start Date</span>
        </label>

        <div class='form-control w-full border-2 border-dashed border-base-300 rounded-box p-4 bg-base-100'>
          <label class='cursor-pointer label justify-between'>
            <span class='label-text text-lg font-semibold'>
              Unlimited Duration
            </span>
            <input
              type='checkbox'
              name='unlimitedDuration'
              class='checkbox checkbox-primary checkbox-lg'
              onInput={handleUnlimitedDuration}
              defaultChecked={getLicense()?.licenseEndAt === -1}
            />
          </label>
          <p class='text-sm text-base-content/70 mt-1'>
            Enable for license without expiration date
          </p>
        </div>

        <label class='floating-label w-full'>
          <input
            name='licenseEndAt'
            type='date'
            class='input input-md w-full disabled:bg-base-200 disabled:text-base-content/50'
            onInput={clearValidity}
            required
            disabled={getLicense()?.licenseEndAt === -1}
            defaultValue={getDateInputValue(getLicense()?.licenseEndAt)}
          />
          <span>End Date</span>
        </label>

        <button type='submit' class='btn btn-primary'>
          Update
          <span
            class={[
              'loading-spinner',
              updateLicense.pending ? 'pending' : '',
            ].join(' ')}
          />
        </button>

        <span
          class={[
            'text-sm',
            (!updateErrorReported.value && updateLicense.error)
              ? 'text-error'
              : '',
          ].join(' ')}
        >
          {(!updateErrorReported.value && updateLicense.error?.message) || ''}
        </span>
      </form>
    </DialogModal>
  )
}

// The footer slot keeps its height even when empty, so the hero value of every
// tile lands on the same baseline whatever each one has to say underneath.
const StatTile = (
  { label, icon: Icon, footer, children }: {
    label: string
    icon: typeof Globe
    footer?: ComponentChildren
    children: ComponentChildren
  },
) => (
  <div class='bg-base-200/50 rounded-box p-4 flex flex-col gap-3 min-h-44'>
    <div class='flex items-start justify-between gap-2'>
      <span class='text-xs uppercase tracking-wider font-semibold text-base-content/60'>
        {label}
      </span>
      <Icon class='w-5 h-5 shrink-0 text-base-content/30' />
    </div>
    <div class='flex-1 flex items-center justify-center text-center'>
      {children}
    </div>
    <div class='min-h-6 flex items-center justify-center text-center text-sm text-base-content/60'>
      {footer}
    </div>
  </div>
)

const DaysLeft = ({ endAt }: { endAt: number }) => {
  const remaining = daysLeft(endAt)
  return (
    <span class={`text-3xl font-bold ${remaining < 30 ? 'text-warning' : ''}`}>
      {remaining} <span class='text-sm font-normal opacity-60'>days left</span>
    </span>
  )
}

const LicenseDetailsCard = ({ details }: { details: LicenseDetails }) => {
  const freeSeats = details.licenseCapacity - details.currentUsage
  const availableSeats = Math.max(0, freeSeats)
  const overusedSeats = Math.max(0, -freeSeats)

  const usedPercent = details.licenseCapacity > 0
    ? Math.round((details.currentUsage / details.licenseCapacity) * 100)
    : 0

  const usageColor = usedPercent > 90
    ? 'text-error'
    : usedPercent > 70
    ? 'text-warning'
    : 'text-success'

  const isUnlimited = details.licenseCapacity === -1
  const isUnlimitedDuration = details.licenseEndAt === -1

  return (
    <div class='card bg-base-100 border border-base-300 mb-6'>
      <div class='card-body'>
        <div class='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2'>
          <div class='flex items-center gap-3'>
            <IdCard class='w-8 h-8 opacity-50 text-primary' />
            <div>
              <h2 class='card-title text-2xl'>
                {details.licenseName || 'License Details'}
              </h2>
              <p class='text-sm text-base-content/60'>
                ID: #{details.licenseId}
              </p>
            </div>
          </div>
          {isAdmin.value && (
            <div class='flex gap-2'>
              <A
                params={{ dialog: 'update-license' }}
                class='btn btn-primary btn-sm gap-2'
                replace
              >
                <Edit class='w-4 h-4' />
                Update
              </A>
              <A
                params={{ dialog: 'delete-license' }}
                class='btn btn-sm btn-outline btn-error gap-2'
                replace
              >
                <Trash2 class='w-4 h-4' />
                Delete
              </A>
            </div>
          )}
        </div>

        <div class='divider my-2' />

        <div class='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
          <StatTile label='Project domain' icon={Globe}>
            <span class='text-lg font-bold break-all'>
              <ProjectDomain domain={details.projectDomain} />
            </span>
          </StatTile>

          <StatTile
            label='Valid period'
            icon={Calendar}
            footer={!isUnlimitedDuration &&
              `${formatDate(details.licenseStartAt)} → ${
                formatDate(details.licenseEndAt)
              }`}
          >
            {isUnlimitedDuration
              ? <span class='badge badge-info badge-lg'>No expiry</span>
              : <DaysLeft endAt={details.licenseEndAt} />}
          </StatTile>

          <StatTile label='License capacity' icon={Users}>
            {isUnlimited
              ? <span class='badge badge-info badge-lg'>Unlimited</span>
              : (
                <span class='text-3xl font-bold'>
                  {details.licenseCapacity}{' '}
                  <span class='text-sm font-normal opacity-60'>seats</span>
                </span>
              )}
          </StatTile>

          <StatTile
            label='Seat allocation'
            icon={ChartPie}
            footer={isUnlimited
              ? `${details.currentUsage} seats used, no limit`
              : (
                <MeterLegend
                  segments={[
                    {
                      value: details.currentUsage,
                      bg: 'bg-primary',
                      label: 'Used',
                    },
                    overusedSeats > 0
                      ? {
                        value: overusedSeats,
                        bg: 'bg-error',
                        label: 'Over capacity',
                      }
                      : {
                        value: availableSeats,
                        bg: 'bg-base-300',
                        label: 'Left',
                      },
                  ]}
                />
              )}
          >
            {isUnlimited
              ? <span class='text-info text-5xl font-bold leading-none'>∞</span>
              : <Donut percent={usedPercent} color={usageColor} />}
          </StatTile>
        </div>
      </div>
    </div>
  )
}

const ParticipantsInScopeTable = () => {
  const handleSelect = (e: Event) => {
    const id = Number((e.target as HTMLSelectElement).value)
    navigate({ params: { scope: id || undefined } })
  }

  const participantCount = selectedScope.value?.participantCount ??
    getLicense()?.currentUsage ?? 0

  return (
    <div class='card bg-base-100 border border-base-300 mb-6'>
      <div class='card-body pb-0'>
        <div class='flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4'>
          <div class='flex items-center gap-3'>
            <Users class='w-6 h-6 text-secondary' />
            <div>
              <h2 class='card-title text-xl'>
                {selectedScope.value?.path ?? 'All Scopes'}
              </h2>
              <div class='flex items-center gap-2 text-sm text-base-content/60'>
                <span class='badge badge-ghost badge-sm'>
                  {participantCount}{' '}
                  participant{participantCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          <div class='flex flex-col sm:flex-row items-stretch sm:items-end gap-3'>
            <div class='form-control w-full lg:w-72'>
              <label class='label'>
                <span class='label-text text-xs uppercase tracking-wider opacity-70'>
                  Filter by scope
                </span>
              </label>
              <select
                value={selectedScopeId.value ?? ''}
                onChange={handleSelect}
                class='select select-bordered select-sm w-full'
              >
                <option value=''>All scopes</option>
                {(getScopes() || []).map((scope) => (
                  <option key={scope.scopeId} value={scope.scopeId}>
                    {scope.path} ({scope.participantCount})
                  </option>
                ))}
              </select>
            </div>

            <a
              href={`/api/license/participants/csv?${new URLSearchParams({
                license: String(getLicense()?.licenseId),
                ...(selectedScopeId.value &&
                  { scope: String(selectedScopeId.value) }),
              })}`}
              download
              class='btn btn-secondary btn-sm gap-2'
            >
              <Download class='w-4 h-4' />
              Export CSV
            </a>
          </div>
        </div>
      </div>

      <div class='divider my-2 px-6' />

      <div class='card-body pt-0'>
        <div class='overflow-x-auto max-h-128 border border-base-200 rounded-box'>
          {getLicenseParticipants.pending
            ? (
              <div class='flex flex-col justify-center items-center h-48 gap-3'>
                <span class='loading loading-lg loading-spinner text-primary' />
                <span class='text-sm text-base-content/60'>
                  Loading participants...
                </span>
              </div>
            )
            : !getParticipants()?.length
            ? (
              <div class='flex flex-col justify-center items-center h-48 gap-4'>
                <div class='bg-base-200 p-4 rounded-full'>
                  <Users class='h-10 w-10 text-base-content/40' />
                </div>
                <div class='text-center'>
                  <h3 class='text-lg font-semibold text-base-content/70'>
                    No Participants Found
                  </h3>
                  <p class='text-sm text-base-content/50'>
                    There are no participants in this scope yet.
                  </p>
                </div>
              </div>
            )
            : (
              <table class='table table-pin-rows'>
                <thead>
                  <tr class='bg-base-200'>
                    <th class='text-xs uppercase tracking-wider'>ID</th>
                    <th class='text-xs uppercase tracking-wider'>
                      External ID
                    </th>
                    <th class='text-xs uppercase tracking-wider'>Login</th>
                    <th class='text-xs uppercase tracking-wider'>Name</th>
                    <th class='text-xs uppercase tracking-wider'>Email</th>
                    <th class='text-xs uppercase tracking-wider'>Joined At</th>
                  </tr>
                </thead>
                <tbody>
                  {getParticipants()?.map((participant, index) => (
                    <tr
                      key={`${participant.participantId}-${index}`}
                      class='hover:bg-base-200/50 transition-colors'
                    >
                      <td>
                        <span class='badge badge-ghost badge-sm font-mono'>
                          #{participant.participantId}
                        </span>
                      </td>
                      <td>
                        <span class='font-mono text-sm text-base-content/70'>
                          {participant.participantExternalId}
                        </span>
                      </td>
                      <td>
                        <span class='font-medium'>
                          {participant.participantLogin}
                        </span>
                      </td>
                      <td>{participant.participantName}</td>
                      <td>
                        <span class='text-sm text-base-content/70'>
                          {participant.participantEmail}
                        </span>
                      </td>
                      <td>
                        <div class='flex items-center gap-1 text-sm'>
                          {formatDate(participant.createdAt)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  )
}

export const LicenseUsagePage = () => {
  const details = getLicense()

  if (getLicenseDetails.pending || getLicenseScopes.pending) {
    return (
      <div class='container mx-auto pt-4 pb-6 px-4 text-center'>
        <span class='loading loading-lg loading-spinner text-primary' />
      </div>
    )
  }

  if (!details) {
    return (
      <div class='container mx-auto pt-4 pb-6 px-4 text-center'>
        <h2 class='text-2xl font-bold text-error'>
          Error: License or Project not found.
        </h2>
        <p>Could not retrieve the details for this license.</p>
      </div>
    )
  }

  return (
    <div class='container mx-auto pt-4 pb-6 px-4'>
      <LicenseDetailsCard details={details} />
      <ParticipantsInScopeTable />
      {isAdmin.value
        ? (
          <>
            <DeleteLicenseModal />
            <UpdateLicenseModal />
          </>
        )
        : null}
    </div>
  )
}
